import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getScoreColor } from "@/lib/scoreUtils";
import { ArrowLeft, ExternalLink, Copy, Check, FileText, Share2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ScoreBreakdown } from "@/components/results/ScoreBreakdown";
import { FavoriteButton } from "@/components/shared/FavoriteButton";
import { CompactScoreBar } from "@/components/shared/CompactScoreBar";
import { getInitials } from "@/lib/utils";

const CATEGORY_COLORS: Record<string, string> = {
  Planning: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  Strategy: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  Research: "bg-green-500/20 text-green-300 border-green-500/30",
  Drafting: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  Analysis: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  Creative: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  Brainstorming: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  Summarizing: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  Unknown: "bg-muted text-muted-foreground border-border",
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function SharedPromptPage() {
  const { scoreId } = useParams<{ scoreId: string }>();
  const navigate = useNavigate();
  const { membership, user } = useAuth();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["shared-prompt", scoreId, membership?.org_id],
    queryFn: async () => {
      if (!membership?.org_id || !scoreId) {
        throw new Error("Missing org or score ID");
      }
      const { data, error } = await supabase.rpc("get_prompt_detail_for_org", {
        _org_id: membership.org_id,
        _score_id: scoreId,
      });
      if (error) throw error;
      if (!data || data.length === 0) throw new Error("Prompt not found");
      return data[0];
    },
    enabled: !!membership?.org_id && !!scoreId,
  });

  // Track share action
  const trackShare = async () => {
    if (!user || !scoreId) return;
    try {
      await (supabase.from("prompt_actions" as any) as any).insert({
        score_id: scoreId,
        user_id: user.id,
        action_type: "share_link",
      });
    } catch (err) {
      console.error("Failed to track share:", err);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: promptTitle || "Check out this prompt",
          text: promptSummary || "A prompt on Cosmo",
          url: shareUrl,
        });
        trackShare();
      } catch (err) {
        // User cancelled or share failed
        if ((err as Error).name !== "AbortError") {
          console.error("Share failed:", err);
        }
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
      trackShare();
    }
  };

  const handleCopy = async () => {
    if (!data?.best_prompt_extracted) return;
    await navigator.clipboard.writeText(data.best_prompt_extracted);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse evaluation_markdown for title/summary
  let promptSummary = "";
  let promptTitle = "";
  if (data?.evaluation_markdown) {
    try {
      const parsed = JSON.parse(data.evaluation_markdown);
      promptSummary = parsed.prompt_summary || "";
      promptTitle = parsed.prompt_title || "";
    } catch (e) {
      // Silently fail
    }
  }

  const chatGptUrl = data?.best_prompt_extracted
    ? `https://chatgpt.com/?prompt=${encodeURIComponent(data.best_prompt_extracted)}`
    : "#";

  const isChat = data?.submission_type === "chat_full";

  // Loading state
  if (isLoading) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="space-y-4">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state - access denied or not found
  if (error || !data) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="bg-muted/30 border-border/50">
            <CardContent className="p-8 text-center">
              <h2 className="text-xl font-semibold mb-2">
                {error?.message === "Prompt not found" ? "Prompt Not Found" : "Access Denied"}
              </h2>
              <p className="text-muted-foreground mb-6">
                {error?.message === "Prompt not found"
                  ? "This prompt doesn't exist or has been deleted."
                  : "You don't have permission to view this prompt. Only members of the same organization can access it."}
              </p>
              <Button onClick={() => navigate("/feed")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Feed
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Back button */}
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Header: Avatar + Name + Time ... Share + Star */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Avatar className="h-10 w-10 border-2 border-primary/20 shrink-0">
              <AvatarFallback className="bg-muted text-muted-foreground text-sm font-medium">
                {getInitials(data.user_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium text-foreground truncate max-w-[150px]">
                {data.user_name || "Unknown"}
              </span>
              <span className="text-muted-foreground text-sm shrink-0">
                · {formatRelativeTime(data.created_at)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={handleShare} title="Share prompt">
              <Share2 className="h-4 w-4" />
            </Button>
            <FavoriteButton scoreId={data.score_id} size="md" />
          </div>
        </div>

        {/* Type badge + Tags */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide mb-5">
          {data.detected_type && (
            <Badge
              variant="outline"
              className={`text-xs border shrink-0 rounded-md ${CATEGORY_COLORS[data.detected_type] || CATEGORY_COLORS["Unknown"]}`}
            >
              {data.detected_type}
            </Badge>
          )}
        </div>

        {/* Score Section */}
        <Card className="bg-muted/30 border-border/50 mb-5">
          <CardContent className="p-4 space-y-3">
            {data.original_score ? (
              <CompactScoreBar inputScore={data.original_score} enhancedScore={data.overall_score} />
            ) : (
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Rubric Score</p>
                  <p className={`text-lg font-bold ${getScoreColor(data.overall_score)}`}>
                    {data.overall_score}
                  </p>
                </div>
              </div>
            )}

            {(promptTitle || promptSummary) && (
              <div className="border-t border-border/50 pt-3">
                {promptTitle && <h3 className="text-sm font-semibold text-foreground mb-1">{promptTitle}</h3>}
                {promptSummary && <p className="text-sm text-foreground/80">{promptSummary}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Improved Prompt */}
        {data.best_prompt_extracted && (
          <div className="space-y-2 mb-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium">Improved Prompt</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8" asChild>
                  <a href={chatGptUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    ChatGPT
                  </a>
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopy} className="h-8">
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
                </Button>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 border border-border">
              <p className="text-sm whitespace-pre-wrap break-words">{data.best_prompt_extracted}</p>
            </div>
          </div>
        )}

        {/* Score breakdown */}
        {data.dimension_scores_json && (
          <ScoreBreakdown
            overallScore={data.overall_score}
            evaluationMarkdown={data.evaluation_markdown || ""}
            dimensionScores={data.dimension_scores_json as Record<string, any>}
            isChat={isChat}
            chatcraftScoresJson={(data.chatcraft_subscores_json as Record<string, number>) || undefined}
            collapsible={true}
          />
        )}
      </div>
    </MainLayout>
  );
}
